import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { FiDownload } from 'react-icons/fi';
import { getAppConfig } from '../services/appConfig.service';
import { isBelow } from '../utils/semver';

/**
 * Launch-time force-update gate (WS4). On a native build below the server's
 * `min_version` floor, it replaces the whole app with a blocking "update required"
 * screen. Web is exempt (always latest). Fails OPEN: any network/config error lets the
 * app through, so a backend hiccup can never brick the client.
 *
 * Note: this only gates builds that CONTAIN it — it can't reach a shipped build that
 * predates the gate. It sets the floor going forward.
 */
export default function VersionGate({ children }: { children: React.ReactNode }) {
  const [storeUrl, setStoreUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const platform = Capacitor.getPlatform();
    if (platform !== 'ios' && platform !== 'android') return;

    void (async () => {
      try {
        const p = platform as 'ios' | 'android';
        const [info, config] = await Promise.all([CapApp.getInfo(), getAppConfig()]);
        if (isBelow(info.version, config.min_version[p])) {
          setStoreUrl(config.store_url[p]);
        }
      } catch {
        // Fail open — never block the app on a config/network error.
      }
    })();
  }, []);

  if (storeUrl) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-(--ee-tw-orange-50-bg) to-(--ee-tw-amber-50-bg) flex items-center justify-center p-6">
        <div className="bg-(--ee-surface) rounded-2xl shadow-xl p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-primary/10 flex items-center justify-center">
            <FiDownload className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-xl font-bold text-(--ee-tw-gray-900) mb-2">Update required</h1>
          <p className="text-sm text-(--ee-tw-gray-600) mb-6">
            This version of EmpathEase is no longer supported. Please update to keep going.
          </p>
          <button
            onClick={() => void Browser.open({ url: storeUrl })}
            className="btn btn-primary w-full"
          >
            Update now
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
